import { useCallback, useEffect } from 'react';

export function useVideoInitialization(videoRef: React.RefObject<HTMLVideoElement | null>, isMuted: boolean): void;
export function useVideoInitialization(video: HTMLVideoElement | null, isMuted: boolean): void;
export function useVideoInitialization(
  videoOrRef: React.RefObject<HTMLVideoElement | null> | HTMLVideoElement | null,
  isMuted: boolean,
) {
  const resolveEl = useCallback(() => {
    if (
      videoOrRef &&
      typeof videoOrRef === 'object' &&
      videoOrRef !== null &&
      'current' in (videoOrRef as { current: unknown })
    ) {
      return (videoOrRef as React.RefObject<HTMLVideoElement | null>).current as HTMLVideoElement | null;
    }
    return (videoOrRef as HTMLVideoElement | null) ?? null;
  }, [videoOrRef]);

  // One-time initialization (mount / element change)
  useEffect(() => {
    const video = resolveEl();
    if (!video) {
      return;
    }

    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const handleLoadedData = () => {
      if (video.paused) {
        video.currentTime = 0.001;
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.load();

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [videoOrRef, resolveEl]);

  // React to mute state without re-loading video
  useEffect(() => {
    const video = resolveEl();
    if (!video) {
      return;
    }
    video.muted = isMuted;
    video.defaultMuted = isMuted;
    if (isMuted) {
      video.setAttribute('muted', '');
    } else {
      video.removeAttribute('muted');
    }
  }, [videoOrRef, isMuted, resolveEl]);
}
